/**
 * The Maps JavaScript API, loaded once when a Google map is first shown (spec 2026-10-22-mapy-google §3.4, decision
 * D4): our own small loader (a script tag with `loading=async` and a callback) instead of a package, and only the
 * types the app uses. A wrong or blocked key calls `gm_authFailure`: the maps then fall back to OpenStreetMap.
 */

export type LatLngLiteral = { lat: number; lng: number };
/** Google gives positions as `LatLng` objects (methods) or literals. */
export type LatLngLike = LatLngLiteral | { lat(): number; lng(): number };
export type MapsListener = { remove(): void };
export interface GMap {
  setCenter(p: LatLngLiteral): void;
  setZoom(z: number): void;
  getZoom(): number | undefined;
  panTo(p: LatLngLiteral): void;
  fitBounds(bounds: GLatLngBounds, padding?: number): void;
  addListener(name: string, fn: (e: { latLng?: LatLngLike | null }) => void): MapsListener;
}
export interface GMarker {
  position: LatLngLike | null | undefined;
  map: GMap | null;
  addListener(name: string, fn: () => void): MapsListener;
}
export interface GPolyline {
  setMap(map: GMap | null): void;
  setOptions(opts: Record<string, unknown>): void;
  addListener(name: string, fn: () => void): MapsListener;
}
export interface GLatLngBounds {
  extend(p: LatLngLiteral): void;
}
export type GoogleMapsNs = {
  Map: new (el: HTMLElement, opts: Record<string, unknown>) => GMap;
  Polyline: new (opts: Record<string, unknown>) => GPolyline;
  LatLngBounds: new () => GLatLngBounds;
  marker: { AdvancedMarkerElement: new (opts: Record<string, unknown>) => GMarker };
};

type MapsWindow = Window & { google?: { maps?: GoogleMapsNs }; gm_authFailure?: () => void; __mekMapsReady?: () => void };

let loading: Promise<GoogleMapsNs> | null = null;
let authFailed = false;
const failureListeners = new Set<() => void>();

/** `{ lat, lng }` of a Google position. */
export const literal = (p: LatLngLike): LatLngLiteral => (typeof p.lat === 'function' ? { lat: (p as { lat(): number }).lat(), lng: (p as { lng(): number }).lng() } : (p as LatLngLiteral));

/** Whether Google refused the key (then every map falls back to OpenStreetMap until the page is reloaded). */
export const googleAuthFailed = () => authFailed;

/** Calls `fn` when Google refuses the key; returns the unsubscribe. */
export function onGoogleAuthFailure(fn: () => void): () => void {
  failureListeners.add(fn);
  return () => failureListeners.delete(fn);
}

/** The Maps JavaScript API with the marker library, in Polish; the same promise for every map. */
export function loadGoogleMaps(key: string): Promise<GoogleMapsNs> {
  if (loading) return loading;
  const w = window as MapsWindow;
  // Google calls it when it refuses the key (also after the script loaded)
  w.gm_authFailure = () => {
    authFailed = true;
    for (const fn of failureListeners) fn();
  };
  loading = new Promise<GoogleMapsNs>((resolve, reject) => {
    if (w.google?.maps?.Map && w.google.maps.marker) return resolve(w.google.maps);
    w.__mekMapsReady = () => (w.google?.maps ? resolve(w.google.maps) : reject(new Error('Mapy Google nie wczytały się')));
    const params = new URLSearchParams({ key, v: 'weekly', loading: 'async', libraries: 'marker', language: 'pl', region: 'PL', callback: '__mekMapsReady' });
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?${params}`;
    script.async = true;
    script.onerror = () => {
      loading = null; // a network hiccup: the next map tries again
      reject(new Error('Mapy Google nie wczytały się'));
    };
    document.head.appendChild(script);
  });
  return loading;
}
